import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, staff, wsUrl, type StaffUser } from '../lib/api.ts';
import { Realtime } from '../lib/realtime.ts';
import { GovHeader, PROGRAM } from './site.tsx';

const DEMO_USERS: Record<string, { user: string; label: string }[]> = {
  clinician: [
    { user: 'dr.ayesha', label: 'Dr Ayesha (clinician)' },
    { user: 'dr.bilal', label: 'Dr Bilal (clinician)' },
  ],
  brief: [
    { user: 'dg', label: 'DG Health — whole program' },
    { user: 'dho.neelum', label: 'DHO Neelum — district' },
  ],
  dashboard: [
    { user: 'dg', label: 'DG Health — whole program' },
    { user: 'dho.neelum', label: 'DHO Neelum — district' },
    { user: 'incharge.neelum', label: 'In-charge RHC Neelum 1 — facility' },
    { user: 'support', label: 'MOVO-X support — device health only' },
  ],
};

export function useStaffSession() {
  const [user, setUser] = useState<StaffUser | null>(null);
  const [checking, setChecking] = useState(Boolean(staff.token()));
  useEffect(() => {
    if (!staff.token()) return;
    staff.me().then(setUser).catch(() => staff.logout()).finally(() => setChecking(false));
  }, []);
  const rt = useMemo(() => (user ? new Realtime(wsUrl({ token: staff.token()! })) : null), [user]);
  useEffect(() => () => rt?.close(), [rt]);
  return {
    user,
    checking,
    rt,
    setUser,
    logout: () => {
      staff.logout();
      setUser(null);
    },
  };
}

export function Login({ surface, title, onLogin }: { surface: 'clinician' | 'dashboard' | 'brief'; title: string; onLogin: (u: StaffUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (u = username, p = password) => {
    setBusy(true);
    setError('');
    try {
      onLogin(await staff.login(u, p));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Cannot reach the server');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login">
      <form className="login-card" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <img src="/brand/seal.svg" width={84} height={84} alt="" style={{ margin: '0 auto' }} />
        <h1>{title}</h1>
        <p className="muted">{PROGRAM.en} · {PROGRAM.sub}<br />{PROGRAM.program}</p>
        {error && <p className="error">{error}</p>}
        <label>Username<input className="input" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} /></label>
        <label>Password<input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <button className="btn btn-primary" disabled={busy || !username || !password}>Sign in</button>
        <div className="demo-logins">
          <div className="muted small">Demo accounts (synthetic data, password <code>demo1234</code>)</div>
          {DEMO_USERS[surface].map((d) => (
            <button type="button" key={d.user} className="btn btn-small" onClick={() => { setUsername(d.user); setPassword('demo1234'); void submit(d.user, 'demo1234'); }}>{d.label}</button>
          ))}
        </div>
        <Link to="/" className="small">← All surfaces</Link>
      </form>
    </div>
  );
}

export function StaffHeader({ title, user, onLogout, live, children }: { title: string; user: StaffUser; onLogout: () => void; live: boolean; children?: ReactNode }) {
  return (
    <div className="s-header gov-header-wrap">
      <GovHeader
        compact
        title={title}
        subtitle={`${PROGRAM.en} · ${PROGRAM.sub}`}
        right={
          <div className="s-tools">
            {children}
            <span className={`live ${live ? 'on' : ''}`} title={live ? 'Live updates connected' : 'Reconnecting'}>{live ? '● Live' : '○ Reconnecting'}</span>
            <span className="s-user">{user.name}<span className="muted small"> · {user.role}{user.district ? ` · ${user.district}` : ''}{user.facility ? ` · ${user.facility}` : ''}</span></span>
            <button className="btn btn-small" onClick={onLogout}>Sign out</button>
          </div>
        }
      />
    </div>
  );
}

export function useLive(rt: Realtime | null) {
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!rt) return;
    const off = rt.on((m) => m.type === 'hello' && setLive(true));
    const iv = setInterval(() => setLive(rt.connected), 2000);
    return () => {
      off();
      clearInterval(iv);
    };
  }, [rt]);
  return live;
}

export const ago = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

export const ageFrom = (birthYear: number | null | undefined) => (birthYear ? `${new Date().getFullYear() - birthYear} y` : '—');
